import { Injectable } from '@angular/core';
import {
  AuthenticationService,
  UserPersonalData,
} from '../authentication.service';
import { SubscriptionPlans } from '../user/user.model';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { resolveTrafficSource } from '../../sales-forecast/sales-forecast-funnel.service';

export type FeatureType = string;

/**
 * Feature constants are compared case-insensitively: the map layers spell
 * some of them lower-cased while the access lists keep the SCREAMING_CASE
 * originals.
 */
function isInList(
  accessibleFeatures: FeatureType[],
  feature: FeatureType
): boolean {
  return (
    accessibleFeatures.includes(feature) ||
    accessibleFeatures.includes(feature.toLowerCase())
  );
}

/**
 * Union above all user statuses.
 * Unknown means the data is still being loaded.
 * Trial means "authorized user with no subscription".
 * Note that subscription plan has priority other 'admin' so admin can check how actual plan looks like.
 */
export type UserAccessStatus =
  | 'unknown'
  | 'unauthorized'
  | 'trial'
  | SubscriptionPlans
  | 'admin';

@Injectable({
  providedIn: 'root',
})
export class UserAccessService {
  // We need it to trigger change detection for pipes in menu toggles
  public isUserPlanUpdated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public accessStatus: BehaviorSubject<UserAccessStatus> =
    new BehaviorSubject<UserAccessStatus>('unknown');

  public isPaidPlanActive: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  public hasPaidPlan(): boolean {
    return this.isPaidPlanActive.value;
  }

  private $userPlan = new BehaviorSubject<SubscriptionPlans | null>(null);

  public userPlan = this.$userPlan.asObservable();

  private isAdmin = false;

  private isTrial = false;
  private createdAt: string | null = null;

  private readonly _isUnauthorized$ = new BehaviorSubject<boolean>(true);
  public readonly isAuthorized$ = this._isUnauthorized$.pipe(map((v) => !v));

  get isUnauthorized(): boolean {
    return this._isUnauthorized$.value;
  }

  private set isUnauthorized(value: boolean) {
    this._isUnauthorized$.next(value);
  }

  constructor(
    private authService: AuthenticationService,
    private http: HttpClient
  ) {
    authService.userPersonalData.subscribe((user) => this.setUserPlan(user!));
  }

  /**
   * @return true if current user has sufficient access to feature/component.
   * Note: frontend access checks are for UX.
   * Backend checks actual access for all the endpoints for security.
   */
  public userHasAccess(feature: FeatureType): boolean {
    if (!this.authService.hasAccessToken()) {
      return isInList(
        SubscriptionPlans.unauthorizedUser.accessibleFeatures,
        feature
      );
    }

    if (this.isTrial) {
      return isInList(
        SubscriptionPlans.trialDescription.accessibleFeatures,
        feature
      );
    }

    // Every subscription includes every indicator on the map, so any active
    // plan is enough - there is no per-plan data list any more.
    return this.$userPlan.value !== null;
  }

  /**
   * @return true if the feature is only reachable with a subscription, i.e.
   * neither the guest nor the free-trial list carries it. Drives the
   * "unlock this with any subscription" prompts.
   */
  public isBySubscription(feature: FeatureType): boolean {
    return !isInList(
      SubscriptionPlans.trialDescription.accessibleFeatures,
      feature
    );
  }

  public setUserPlan(info: UserPersonalData | null): void {
    if (info) {
      this.isUserPlanUpdated.next(false);
      this.$userPlan.next(info?.subscription?.plan || null);
      this.isAdmin = info?.isAdmin || false;
      this.isTrial = !this.isAdmin && this.$userPlan.value === null;
      this.isUnauthorized = false;
      this.createdAt = info.createdAt || null;
      if (this.$userPlan.value !== null) {
        this.isPaidPlanActive.next(true);
      }

      // this.updateTrialExpiredStatus(info.createdAt);

      // if (this.isTrial && this.subscriptionsService.accessibleFeaturesList.length === 0) {
      //   this.subscriptionsService.setAccessibleFeaturesId()
      // } else if (!this.isTrial) {
      //   this.subscriptionsService.setAccessibleFeaturesAll()
      // }

      this.isUserPlanUpdated.next(true);

      // Plan takes priority over admin so an admin with a subscription gets their plan features
      this.updateStatus(
        this.$userPlan.value ?? (this.isAdmin ? 'admin' : 'trial')
      );
    } else if (info === null) {
      this.isUserPlanUpdated.next(false);

      this.$userPlan.next(null);
      this.isAdmin = false;
      this.isTrial = false;
      this.isUnauthorized = true;
      this.isPaidPlanActive.next(false);
      this.isUserPlanUpdated.next(true);

      this.updateStatus('unauthorized');
    }
  }

  private updateStatus(status: UserAccessStatus): void {
    if (this.accessStatus.value !== status) {
      this.accessStatus.next(status);
    }
  }

  public getUserPlan(): SubscriptionPlans | null {
    return this.$userPlan.value;
  }

  public getIsAdmin(): boolean {
    return this.isAdmin;
  }

  public getIsUnauthorized(): boolean {
    return this.isUnauthorized;
  }

  public getIsAuthorized(): boolean {
    return !this.isUnauthorized;
  }

  public getIsTrial(): boolean {
    return this.isTrial;
  }

  /**
   * Change subscription of given user.
   * Works only for admins which is assured by backend.
   */
  public grantSubscription(
    toWhomEmail: string,
    plan: SubscriptionPlans,
    durationDays = 1_000,
    sendEmail = false
  ): Observable<any> {
    return this.http.post(
      `${environment.apiUrl}user/subscription/grant` +
        `?existingUserEmail=${toWhomEmail}&plan=${plan}&durationDays=${durationDays}&sendEmail=${sendEmail}`,
      {}
    );
  }

  public triggerMapLoadedEvent(): void {
    this.http
      .post(`${environment.apiUrl}user/metrics/events/map-loaded`, {})
      .subscribe();
  }

  /**
   * Logs SITE_OPENED, the map app's own equivalent of the sales-forecast
   * funnel's PAGE_OPENED - the funnel's page-view logic lives in
   * SalesForecastFunnelService, but resolveTrafficSource() is shared: a paid
   * click that lands on the map, not a funnel page, deserves the same
   * attribution.
   *
   * Carries the source as a query param, not a JSON body: the backend
   * endpoint binds it with @RequestParam, matching /events/page.
   */
  public startPageView(): void {
    const source = resolveTrafficSource();
    const url =
      `${environment.apiUrl}user/metrics/events/site-opened` +
      (source ? `?source=${encodeURIComponent(source)}` : '');
    this.http.post(url, {}).subscribe();
  }
}
